import 'altcha';
import { register } from '../core/auth/auth-api';
import { API_BASE } from '../core/network/api';
import { t } from './i18n/i18n';

const devUsername = import.meta.env.DEV ? 'admin' : '';
const devEmail = import.meta.env.DEV ? (import.meta.env.VITE_DEV_EMAIL || 'player1@gamepreview.it.com') : '';
const devPassword = import.meta.env.DEV ? (import.meta.env.VITE_DEV_PASSWORD || 'Player123!') : '';

type PasswordStrength = 'empty' | 'weak' | 'fair' | 'good' | 'strong';

function computePasswordStrength(pw: string): PasswordStrength {
  if (!pw) return 'empty';
  let types = 0;
  if (/[a-z]/.test(pw)) types++;
  if (/[A-Z]/.test(pw)) types++;
  if (/[0-9]/.test(pw)) types++;
  const hasSpecial = /[^a-zA-Z0-9]/.test(pw);
  if (hasSpecial) types++;
  if (pw.length < 8 || types <= 1) return 'weak';
  if (pw.length >= 12 && types >= 3 && hasSpecial) return 'strong';
  if (types >= 3) return 'good';
  return 'fair';
}

const STRENGTH_CONFIG: Record<Exclude<PasswordStrength, 'empty'>, { label: () => string; width: string; color: string }> = {
  weak:   { label: () => t('auth.strength.weak'),   width: '25%',  color: '#ef4444' },
  fair:   { label: () => t('auth.strength.fair'),   width: '50%',  color: '#f59e0b' },
  good:   { label: () => t('auth.strength.good'),   width: '75%',  color: '#e2601f' },
  strong: { label: () => t('auth.strength.strong'), width: '100%', color: '#3ecf8e' },
};

export function renderRegister(root: HTMLElement, onSuccess: () => void): () => void {
  root.innerHTML = `
    <div class="min-h-screen grid place-items-center px-5 py-10 bg-auth-gradient">
      <form class="w-full max-w-[420px] bg-auth-card border border-border rounded-lg p-8 shadow-lift" id="register-form" novalidate>
        <h1 class="mb-2 font-heading text-2xl text-gold tracking-wide">${t('auth.register.title')}</h1>
        <p class="mb-6 text-sm text-text-dim">${t('auth.register.subtitle')}</p>
        <div id="register-error" class="mb-3.5 px-3 py-2.5 bg-error/[0.08] border border-error/40 rounded text-error text-[13px]" hidden></div>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">${t('auth.register.username')}</span>
          <input type="text" name="username" minlength="3" maxlength="32" pattern="[a-zA-Z0-9_-]+" value="${devUsername}"
            class="w-full px-3 py-2.5 bg-surface text-text border border-border rounded outline-none transition-colors duration-[120ms] focus:border-gold focus:shadow-glow" />
        </label>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">${t('auth.email')}</span>
          <input type="email" name="email" autocomplete="email" required value="${devEmail}"
            class="w-full px-3 py-2.5 bg-surface text-text border border-border rounded outline-none transition-colors duration-[120ms] focus:border-gold focus:shadow-glow" />
        </label>
        <label class="block mb-1">
          <span class="block mb-1.5 text-[13px] text-text-dim">${t('auth.register.password')}</span>
          <input type="password" name="password" id="register-password" autocomplete="new-password" minlength="8" required value="${devPassword}"
            class="w-full px-3 py-2.5 bg-surface text-text border border-border rounded outline-none transition-colors duration-[120ms] focus:border-gold focus:shadow-glow" />
        </label>
        <div class="mb-3.5">
          <div class="flex items-center gap-2 h-5">
            <div class="flex-1 h-1 rounded-full bg-border overflow-hidden">
              <div id="register-strength-bar" class="h-full rounded-full transition-all duration-200" style="width:0%"></div>
            </div>
            <span id="register-strength-label" class="text-[10px] shrink-0" style="min-width:3rem"></span>
          </div>
        </div>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">${t('auth.register.repeatPassword')}</span>
          <input type="password" name="repeatPassword" id="register-repeat-password" autocomplete="new-password" minlength="8" required value="${devPassword}"
            class="w-full px-3 py-2.5 bg-surface text-text border border-border rounded outline-none transition-colors duration-[120ms] focus:border-gold focus:shadow-glow" />
        </label>
        <div class="mb-3.5">
          <altcha-widget challenge="${API_BASE}/auth/altcha-challenge" auto="onload" name="altcha"></altcha-widget>
        </div>
        <div class="mb-3.5 flex items-start gap-2">
          <input type="checkbox" id="register-terms" name="terms"
            class="mt-0.5 accent-gold shrink-0 cursor-pointer" />
          <span class="text-[10px] text-text-dim leading-relaxed">${t('auth.register.terms', {
            terms: `<a href="/legal/terms" class="text-gold hover:underline">${t('auth.register.termsLink')}</a>`,
            privacy: `<a href="/legal/privacy" class="text-gold hover:underline">${t('auth.register.privacyLink')}</a>`,
          })}</span>
        </div>
        <button type="submit" class="inline-block w-full p-3 bg-gold text-bg font-bold border-0 rounded transition-colors duration-[120ms] hover:bg-gold-bright disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed" id="register-btn" disabled>${t('auth.createAccount')}</button>
        <p class="mt-5 text-[13px] text-text-dim text-center">
          ${t('auth.register.haveAccount')} <a href="/login">${t('auth.login.submit')}</a>
        </p>
      </form>
    </div>
  `;

  const form = root.querySelector('#register-form') as HTMLFormElement;
  const errEl = root.querySelector('#register-error') as HTMLDivElement;
  const btn = root.querySelector('#register-btn') as HTMLButtonElement;
  const pwInput = root.querySelector('#register-password') as HTMLInputElement;
  const strengthBar = root.querySelector('#register-strength-bar') as HTMLDivElement;
  const strengthLabel = root.querySelector('#register-strength-label') as HTMLSpanElement;
  const termsCheckbox = root.querySelector('#register-terms') as HTMLInputElement;

  function updateStrength(): void {
    const strength = computePasswordStrength(pwInput.value);
    if (strength === 'empty') {
      strengthBar.style.width = '0%';
      strengthBar.style.backgroundColor = '';
      strengthLabel.textContent = '';
      return;
    }
    const cfg = STRENGTH_CONFIG[strength];
    strengthBar.style.width = cfg.width;
    strengthBar.style.backgroundColor = cfg.color;
    strengthLabel.textContent = cfg.label();
    strengthLabel.style.color = cfg.color;
  }

  function updateSubmitState(): void {
    btn.disabled = !termsCheckbox.checked;
  }

  pwInput.addEventListener('input', updateStrength);
  termsCheckbox.addEventListener('change', updateSubmitState);

  updateStrength();
  updateSubmitState();

  const handler = async (e: Event) => {
    e.preventDefault();
    errEl.hidden = true;

    const fd = new FormData(form);
    const password = String(fd.get('password') ?? '');
    const repeatPassword = String(fd.get('repeatPassword') ?? '');

    if (password !== repeatPassword) {
      errEl.textContent = t('auth.register.passwordsMismatch');
      errEl.hidden = false;
      return;
    }

    const altchaPayload = fd.get('altcha') as string | null;
    if (!altchaPayload) {
      errEl.textContent = t('auth.waitVerification');
      errEl.hidden = false;
      return;
    }
    btn.disabled = true;
    btn.textContent = t('auth.register.submitting');
    try {
      await register(
        String(fd.get('email') ?? ''),
        password,
        altchaPayload,
        (fd.get('username') || undefined) as string | undefined,
      );
      onSuccess();
    } catch (err) {
      errEl.textContent = (err as Error).message;
      errEl.hidden = false;
      btn.disabled = !termsCheckbox.checked;
      btn.textContent = t('auth.createAccount');
    }
  };
  form.addEventListener('submit', handler);

  return () => {
    form.removeEventListener('submit', handler);
    pwInput.removeEventListener('input', updateStrength);
    termsCheckbox.removeEventListener('change', updateSubmitState);
  };
}
